import ChangePasswordModal from "@/components/UserSettings/ChangePasswordModal";
import { MdPhoneAndroid } from "react-icons/md";
import { IoIosMail } from "react-icons/io";
import ChangePhoneNumberModal from "@/components/UserSettings/ChangePhoneNumberModal";
import { useEffect, useState } from "react";
import ErrorMessage from "@/components/ErrorMessage";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import ChangeEmailModal from "@/components/UserSettings/ChangeEmailModal";
import { Skeleton } from "@/components/ui/skeleton";
import { IoText } from "react-icons/io5";
import ChangeNameModal from "@/components/UserSettings/ChangeNameModal";

import { useTranslation } from "react-i18next";
import ProfileImage from "@/components/ProfileImage";

const UserSettings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [userEmail, setUserEmail] = useState<string>("");
  const [userPhone, setUserPhone] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [userImage, setUserImage] = useState<string|null>(null);
  const [needRefetch, setNeedRefetch] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const url = `${BASE_URL}/api/dashboard/users?locale=${lang}`;

  useEffect(() => {
    if (!token) return;

    setIsLoading(true);
    setError(null);
    setNeedRefetch(false);

    const fetchUserContactInfo = async () => {
      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: `${token}`,
          },
        });

        if (!response.data.success) {
          toast.error(
            response.data.error ||
              (t("userSettings.unexpectedError") as string),
          );

          return;
        }

        setUserEmail(response.data.email);
        setUserPhone(response.data.phone);

        setUserName(response.data.username);
        setUserImage(response?.data?.avatar || null);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          const ErrorMsg =
            error.response?.data?.error ||
            (t("userSettings.unexpectedError") as string);
          setError(ErrorMsg);
          toast.error(ErrorMsg);
        } else {
          setError(t("userSettings.unexpectedError") as string);
          toast.error(t("userSettings.unexpectedError") as string);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserContactInfo();
  }, [needRefetch, token, t, url]);

  if (isLoading)
    return (
      <div className="flex flex-col items-center gap-6 rounded-md border bg-white p-5">
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          {/* Header Skeleton */}
          <Skeleton className="h-8 w-48 rounded-md bg-gray-200 dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-10 w-32 rounded-md bg-gray-200 dark:bg-slate-600 dark:bg-opacity-35" />
        </div>

        <div>
          <Skeleton className="size-[100px] rounded-full bg-gray-200 text-gray-400 dark:bg-slate-600 dark:bg-opacity-35" />
        </div>

        <div className="flex w-full flex-col gap-3">
          <div className="relative w-full">
            <Skeleton className="h-10 w-full rounded-md bg-gray-200 dark:bg-slate-600 dark:bg-opacity-35" />

            <Skeleton className="absolute end-2 top-1/2 h-8 w-24 -translate-y-1/2 transform rounded-md bg-gray-200 dark:bg-slate-600 dark:bg-opacity-35" />
          </div>
          <div className="relative w-full">
            <Skeleton className="h-10 w-full rounded-md bg-gray-200 dark:bg-slate-600 dark:bg-opacity-35" />
            <Skeleton className="absolute end-2 top-1/2 h-8 w-24 -translate-y-1/2 transform rounded-md bg-gray-200 dark:bg-slate-600 dark:bg-opacity-35" />
          </div>

          <div className="relative w-full">
            <Skeleton className="h-10 w-full rounded-md bg-gray-200 dark:bg-slate-600 dark:bg-opacity-35" />
            <Skeleton className="absolute end-2 top-1/2 h-8 w-24 -translate-y-1/2 transform rounded-md bg-gray-200 dark:bg-slate-600 dark:bg-opacity-35" />
          </div>
        </div>
      </div>
    );
  if (error) return <ErrorMessage msg={error} />;

  return (
    <div className="flex flex-col items-center gap-6 rounded-md border bg-white p-5">
      {/* Header and Change Password */}
      <div className="flex w-full flex-wrap items-center justify-between gap-3">
        <h1 className="self-start text-2xl font-semibold">
          {t("userSettings.profile")}
        </h1>
        <ChangePasswordModal setNeedRefetch={setNeedRefetch} />
      </div>

      {/* Profile Image */}
      <ProfileImage
        size={130}
        imageUrl={userImage}
        withUpdate={true}
        setNeedRefetch={setNeedRefetch}
      />

      {/* (Phone & Email) input fileds */}
      <div className="flex w-full flex-col gap-3">
        {/* user Name */}
        <div className="relative w-full">
          <input
            type="text"
            readOnly
            value={userName}
            className="w-full rounded-md border border-gray-300 p-2 pe-[85px] ps-8"
          />
          {/* Icon */}
          <span className="absolute start-2 top-1/2 -translate-y-1/2 transform">
            <IoText size={20} className="text-gray-400" />
          </span>
          {/* Update */}
          <ChangeNameModal setNeedRefetch={setNeedRefetch} name={userName} />
        </div>
        {/* Phone Number */}
        <div className="relative w-full">
          <input
            type="number"
            readOnly
            value={userPhone}
            className="w-full rounded-md border border-gray-300 p-2 pe-[85px] ps-8"
          />
          {/* Icon */}
          <span className="absolute start-2 top-1/2 -translate-y-1/2 transform">
            <MdPhoneAndroid size={20} className="text-gray-400" />
          </span>
          {/* Update */}
          <ChangePhoneNumberModal
            setNeedRefetch={setNeedRefetch}
            phone={userPhone}
          />
        </div>

        {/* email */}
        <div className="relative w-full">
          <input
            type="email"
            readOnly
            value={userEmail}
            className="w-full rounded-md border border-gray-300 p-2 pe-[85px] ps-8"
          />
          <span className="absolute start-2 top-1/2 -translate-y-1/2 transform">
            <IoIosMail size={20} className="text-gray-400" />
          </span>
          <ChangeEmailModal setNeedRefetch={setNeedRefetch} email={userEmail} />
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
